/**
 * Client-side estimates for the Frame picker (FRAME_PICKER_DESIGN.md §3.2, §6.2 `media/`, §6.5).
 *
 * Design §3.2: "ironman is authoritative; the browser mirrors it for the estimate". Every count
 * here reproduces the arithmetic in ironman
 * `conversions/strategy/impl/FramePickerRequest.kt` (`parseRange`, :218 / :223) with the SAME
 * double operations in the SAME order, so the number the panel shows is the number the edge
 * REQUESTS — including the floating-point corner cases (no epsilon is added that ironman does not
 * add).
 *
 * Round 6 (R6-I5): the number the edge DELIVERS can be smaller than the number it REQUESTS. ironman asks ffmpeg
 * for `req.frames` (`-frames:v <n>`, `tile=…:nb_frames=<n>`) and then only TRUNCATES the listing
 * (`frames.take(req.frames)`) — it never pads.
 *
 * R14 (BUG-002, ironman `1ac91e8d`, live on QA 2026-09-23): the sequence and the sheet are cut with
 * `-ss <start> -to <end + I/2> -i <input> -vf select='gte(t+0.001\,selected_n*<I>)' -fps_mode passthrough -frames:v <n>`
 * (`FrameExtraction.kt#buildSequenceCommand` / `#buildSheetCommand`, `selectNode`): `t` is the source pts relative
 * to the `-ss` instant, so output `n` is the FIRST source frame with `t + 0.001 ≥ n · I` inside the `-to` window --
 * the ONE-frame path's "first frame with pts ≥ the instant" semantics. Before it the `fps=1/<I>` filter filled each
 * slot with the LAST frame that rounded to it (every frame ⌈I·fps/2⌉ − 1 late, +4 at 30 fps / ⅓ s) and never wrote
 * the clip's end instant; the round-6 / round-9 counts here ("10 s at 1 s → 10", "Exactly 12 → 11") modelled THAT
 * filter and are gone with it. `deliveredFrames`, `countSampleEnd` and `frameTimes` model the select rule; the
 * jest oracle (`media/frame-oracle.spec.ts`) runs the filter literally over a synthetic clip and checks them.
 *
 * React-free and pure.
 */
import { ASSUMED_FPS } from './timeline';

export type Spacing = 'BY_INTERVAL' | 'BY_COUNT';

/** Design §3.2: "A count < 2 is refused (`RANGE_INVALID`)". Ironman: `if (n < 2) throw …`. */
export const MIN_FRAME_COUNT = 2;

/**
 * Design §6.2 `estimate.ts`: "'How long' from a fixed 0.25 s/frame floored at 'a few seconds'".
 * The floor is copy (owned by `copy.ts`); this is the per-frame constant.
 */
export const SECONDS_PER_FRAME = 0.25;

/**
 * Clamp a range to the clip exactly as ironman does:
 * `start = (screenshotStartTime ?: 0.0).coerceIn(0.0, duration)`,
 * `end = (screenshotEndTime ?: duration).coerceIn(0.0, duration)`.
 * Design §3.2: "Range = `[start, end]` clamped to `[0, duration]` … empty = clip start / clip end".
 * `null`/`undefined`/NaN stand for the empty option value.
 */
export function clampRange(
    start: number | null | undefined,
    end: number | null | undefined,
    duration: number,
): { start: number; end: number } {
    const d = Number.isFinite(duration) && duration > 0 ? duration : 0;
    const s = isNum(start) ? start : 0;
    const e = isNum(end) ? end : d;
    return { start: coerceIn(s, 0, d), end: coerceIn(e, 0, d) };
}

/**
 * Design §3.2: "`start ≥ end` after clamping → `RANGE_INVALID`". Ironman: `if (start >= end) throw`.
 */
export function isRangeValid(start: number, end: number): boolean {
    return isNum(start) && isNum(end) && start < end;
}

/**
 * The interval a `BY_COUNT` request resolves to. Design §3.2: "`BY_COUNT` → the interval is
 * `(end − start) / (count − 1)`, first frame at `start`, last at `end`". Ironman:
 * `interval = (end - start) / (n - 1)`. Returns `NaN` when the count is refused (< 2) or the
 * range is invalid, so a caller can never show a number the edge would not compute.
 */
export function deriveInterval(count: number, start: number, end: number): number {
    if (!isRangeValid(start, end) || !Number.isInteger(count) || count < MIN_FRAME_COUNT) { return NaN; }
    return (end - start) / (count - 1);
}

/**
 * Number of output frames ironman REQUESTS for a sequence or sheet (`req.frames`, the number its
 * §3.2 refusals and `-frames:v` / `nb_frames` read).
 *
 * - `BY_INTERVAL`: design §3.2 "`screenshotFramerate` is seconds per frame from `start`, last
 *   frame ≤ `end`". Ironman: `frames = floor((end - start) / i).toInt() + 1` (FramePickerRequest.kt:223).
 * - `BY_COUNT`: design §3.2 "first frame at `start`, last at `end`". Ironman: `frames = n` (:218).
 *
 * Returns 0 where ironman would refuse (`start ≥ end`, `interval ≤ 0`, `count < 2`) or where an
 * input is not a finite number, so the estimate line can read "0 frames" and the panel can show
 * the §3.5 message instead of a fabricated count.
 */
export function requestedFrames(spacing: Spacing, interval: number, count: number, start: number, end: number): number {
    if (!isRangeValid(start, end)) { return 0; }
    if (spacing === 'BY_COUNT') {
        if (!Number.isInteger(count) || count < MIN_FRAME_COUNT) { return 0; }
        return count;
    }
    if (!isNum(interval) || interval <= 0) { return 0; }
    // Same double arithmetic as Kotlin's `floor((end - start) / i).toInt() + 1` — no epsilon.
    return Math.floor((end - start) / interval) + 1;
}

/**
 * R14 (BUG-002): the epsilon in ironman's select expression, `select='gte(t+0.001\,selected_n*<I>)'`
 * (`FrameExtraction.kt#selectNode`, ironman `1ac91e8d`): output `n` is the first source frame whose pts, relative
 * to the `-ss` instant, is within a millisecond below `n · I` or past it. It absorbs the 3-decimal `-ss` and float
 * noise; it stays unambiguous while a frame is longer than 1.5 ms (fps < 660).
 */
export const SELECT_EPS_S = 0.001;

/**
 * A billionth of a frame: the slack for comparing a grid time `k / fps` with an instant computed in another order
 * (`(start + n · I − 0.001) · fps`); float noise is ~1e-13 frames, a real frame boundary is never that close. NOT
 * `timeline.ts#FRAME_EPS` (a thousandth of a frame): that one absorbs an element's µs-truncated `currentTime`; the
 * server compares exact stream timestamps, and 33 µs of slack would name a frame the select rule does not take when
 * a BY_COUNT instant lands that close to a boundary.
 */
const GRID_TOL_FRAMES = 1e-9;

/** ironman `fmt6` -- the interval as the select expression carries it (6 decimals). */
export function argvInterval(interval: number): number {
    return Math.round(interval * 1e6) / 1e6;
}

/** ironman `fmt` -- a `-ss` / `-to` token (3 decimals). */
function argvSeconds(t: number): number {
    return Math.round(t * 1000) / 1000;
}

/**
 * R14 (BUG-002): the 0-based source frame (on the `k / fps` grid, pts `k / fps`) ironman's select rule writes as
 * output `n` of a sequence or sheet whose WRITTEN start is `start` and whose interval is `interval`: `-ss <start>`
 * keeps the frames with pts ≥ start, `t` is the pts relative to it, and `select='gte(t+0.001\,selected_n*<fmt6(I)>)'`
 * with `-fps_mode passthrough` takes, for each `n`, the first not-yet-taken frame with `t + 0.001 ≥ n · I6` -- the
 * first grid frame at or after `start + n · I6 − SELECT_EPS_S`, and never a frame at or before the one output `n − 1`
 * took, which is what makes an interval SHORTER than a frame deliver every frame once (the `fps` filter duplicated
 * them). Whether that frame exists (the clip's end) and lies inside the `-to` window is `deliveredFrames`'s question;
 * this one only names it.
 */
export function selectedFrameIndex(n: number, start: number, interval: number, fps: number): number {
    const first = Math.max(0, Math.ceil(start * fps - GRID_TOL_FRAMES));
    const byInstant = Math.ceil((start + n * argvInterval(interval) - SELECT_EPS_S) * fps - GRID_TOL_FRAMES);
    return Math.max(first + n, byInstant);
}

/**
 * The last source frame on the grid: design §6.2 "total frames = `round(duration × fps)`" (the picker's own
 * `Timeline.frameCount`, the counter's "of N"), less one; never below 0. Not `floor((duration − 1/fps) · fps)`: a
 * container duration a tenth of a millisecond short of `N / fps` would lose the clip's last frame.
 */
export function lastGridFrame(duration: number, fps: number): number {
    return Math.max(0, Math.round(duration * fps) - 1);
}

/**
 * How many of the `requested` frames the edge's ffmpeg WRITES (the header): R14 (BUG-002, ironman `1ac91e8d`), the
 * select rule. Output `n` (0-based, `n < requested`) is written iff the frame `selectedFrameIndex` names exists --
 * it is not past the clip's last frame (`lastGridFrame`) -- and its pts is inside the `-to <fmt(end + I/2)>` window
 * (it always is for `n · I ≤ end − start` once `I ≥ 1/fps`; modelled anyway). Then ironman's own truncation to
 * `requested`. `start` is the WRITTEN start (a quarter frame early, `state/picker-state.ts#rangeStartWrite`), as the
 * callers pass it. Without a rate the grid is `ASSUMED_FPS` (30), the rate ironman assumes when the probe has none
 * (§3.2) and the picker's own provisional `Timeline` runs on: the clip's END INSTANT is never a frame on any real
 * clip, so a "frame at every instant" model would say 11 for 10 s at 1 s while the header, once the rate lands, says
 * 10. `duration` unknown (`Infinity`, NaN, ≤ 0) → `requested`.
 */
export function deliveredFrames(requested: number, interval: number, start: number, end: number, duration: number, fps?: number | null): number {
    if (!(requested > 0) || !isNum(interval) || interval <= 0 || !isRangeValid(start, end)) { return 0; }
    const req = Math.floor(requested);
    if (!isNum(duration) || duration <= 0) { return req; }
    const rate = isNum(fps) && fps > 0 ? fps : ASSUMED_FPS;
    const last = lastGridFrame(duration, rate);
    const stop = argvSeconds(end + interval / 2); // `-to` as an input option: frames with pts < stop are read.
    const ok = (n: number) => { const k = selectedFrameIndex(n, start, interval, rate); return k <= last && k / rate < stop; };
    // Both bounds are monotone in `n`; the closed form for the last written `n`, then a step either way for float noise.
    const cap = Math.min(last, Math.ceil(stop * rate - GRID_TOL_FRAMES) - 1);
    const first = Math.max(0, Math.ceil(start * rate - GRID_TOL_FRAMES));
    let n = Math.min(req, cap - first + 1, Math.floor(((cap + GRID_TOL_FRAMES) / rate - start + SELECT_EPS_S) / argvInterval(interval)) + 1);
    n = Math.max(0, Math.min(req, Number.isFinite(n) ? n : 0));
    while (n < req && ok(n)) n++;
    while (n > 0 && !ok(n - 1)) n--;
    return n;
}

/**
 * Where a BY_COUNT range that reaches the clip's END is SAMPLED to (and what the request carries,
 * `state/picker-state.ts#toOptionWrites`), so the client's count, the grid, the sheet and the ZIP agree at the number
 * the user typed. R14 (BUG-002): with the clip's `fps` known the sampled end is the LAST FRAME's own start
 * (`lastGridFrame / fps`, never below `start`) -- a real frame, so the last requested frame is written under the
 * select rule (`start + (n − 1) · I − 1 ms` names it) and every one before it. Without a rate the round-9 pull stays:
 * `start + (end − start) · (n − 1) / (n − 0.5)`, less 0.1 % -- the end instant is not a frame on any real clip, and
 * without the grid the picker cannot say where the last one is. A range short of the clip's end is returned
 * unchanged: its end is a frame the user chose.
 */
export function countSampleEnd(count: number, start: number, end: number, duration: number, fps?: number | null): number {
    if (!isRangeValid(start, end) || !Number.isInteger(count) || count < MIN_FRAME_COUNT) { return end; }
    if (!isNum(duration) || duration <= 0 || end < duration - 1e-6) { return end; }
    if (isNum(fps) && fps > 0) { return Math.max(start, Math.min(end, lastGridFrame(duration, fps) / fps)); }
    const pulled = start + (end - start) * ((count - 1) / (count - 0.5)) * (1 - 1e-3);
    return Math.max(start, Math.min(end, pulled));
}

/**
 * The seconds between two frames a request resolves to: the interval itself, or `(end − start) / (count − 1)`
 * -- over the SAMPLED end (`countSampleEnd`) when the clip's length is known and the range reaches it.
 */
export function stepOf(spacing: Spacing, interval: number, count: number, start: number, end: number, duration: number = Number.POSITIVE_INFINITY, fps?: number | null): number {
    return spacing === 'BY_COUNT' ? deriveInterval(count, start, countSampleEnd(count, start, end, duration, fps)) : interval;
}

/**
 * Number of output frames the user GETS: `requestedFrames` when the clip's length is unknown, else
 * `deliveredFrames` over it. Every panel number — the header's "Convert N frames", the progress "0 of N", the
 * sheet's cells — reads this one so they agree with the ZIP and the JPEG. `fps` (R14): the clip's frame rate puts
 * the frames on their grid; without it `ASSUMED_FPS` does (`deliveredFrames`).
 */
export function estimateFrames(spacing: Spacing, interval: number, count: number, start: number, end: number, duration: number = Number.POSITIVE_INFINITY, fps?: number | null): number {
    const requested = requestedFrames(spacing, interval, count, start, end);
    if (requested === 0) { return 0; }
    return deliveredFrames(requested, stepOf(spacing, interval, count, start, end, duration, fps), start, end, duration, fps);
}

/**
 * The frame timestamps a sequence produces, for "Frames you will get" (§6.2 `frames-grid`) and the sheet preview.
 * With `fps` (R14, BUG-002): the START of each frame the select rule writes (`selectedFrameIndex`), so the preview
 * shows the frames the ZIP will hold. Without it: `BY_INTERVAL` → `start + k·interval` for `k < frames`;
 * `BY_COUNT` → `start + k·(end−start)/(count−1)`, the last pinned on the (sampled) end. Empty where `estimateFrames`
 * is 0. (`state/picker-state.ts#frameTimes` is the picker's own, over the WRITTEN range.)
 */
export function frameTimes(spacing: Spacing, interval: number, count: number, start: number, end: number, duration: number = Number.POSITIVE_INFINITY, fps?: number | null): number[] {
    const requested = requestedFrames(spacing, interval, count, start, end);
    if (requested === 0) { return []; }
    const step = stepOf(spacing, interval, count, start, end, duration, fps);
    const n = deliveredFrames(requested, step, start, end, duration, fps);
    const out = new Array<number>(n);
    if (isNum(fps) && fps > 0) {
        const last = isNum(duration) && duration > 0 ? lastGridFrame(duration, fps) : Number.POSITIVE_INFINITY;
        for (let k = 0; k < n; k++) { out[k] = Math.min(selectedFrameIndex(k, start, step, fps), last) / fps; }
        return out;
    }
    for (let k = 0; k < n; k++) { out[k] = start + k * step; }
    // Pin the last frame at the (sampled) end exactly -- the user's `end`, or the pulled-in end over a whole clip (round 9, #5).
    if (spacing === 'BY_COUNT' && n === requested) { out[n - 1] = countSampleEnd(count, start, end, duration, fps); }
    return out;
}

/**
 * Sheet geometry. Design §3.2: "Sheet = `frames` cells (count or interval) in `sheetColumns`
 * columns, rows = ceil(frames / columns) … A ragged last row is allowed and shown in the UI
 * ('30 in 4 columns = 7 rows + 2')".
 *
 * `cells` is the number of frames placed (the design's "frames cells"), not `cols × rows`;
 * `ragged` is how many cells the last row holds when it is not full (0 when every row is full),
 * so "30 in 4 columns" reads `{ cols: 4, rows: 8, cells: 30, ragged: 2 }` — seven full rows plus
 * a row of two. Ironman: `rows = ceil(frames / cols)` in `FramePickerRequest.parse` (P2.1).
 */
export function estimateSheetCells(cols: number, count: number): { cols: number; rows: number; cells: number; ragged: number } {
    const c = Number.isInteger(cols) && cols > 0 ? cols : 0;
    const n = Number.isInteger(count) && count > 0 ? count : 0;
    if (c === 0 || n === 0) { return { cols: c, rows: 0, cells: 0, ragged: 0 }; }
    const rows = Math.ceil(n / c);
    const rem = n % c;
    return { cols: c, rows, cells: n, ragged: rem };
}

/** Ironman `tile=…:padding=8:margin=16` (§4.2): the sheet's outer margin, one per side. */
export const SHEET_MARGIN_PX = 16;
/** Ironman `tile=…:padding=8`: the gap between two adjacent cells. */
export const SHEET_PADDING_PX = 8;

/**
 * Sheet cell width. Design §3.2: "cell width = `floor((sheetWidth − margins − padding) / columns)`,
 * source aspect kept". `margins` and `padding` are the sheet's total horizontal chrome in pixels
 * and default to what ironman's sheet builder uses — `2 · 16` of margin and `(cols − 1) · 8` of
 * padding — so `sheetCellWidth(1920, 4)` is 466, the `scale=466:-2` the edge emits. A caller that
 * renders with different chrome passes its own totals.
 */
export function sheetCellWidth(sheetWidth: number, cols: number, margins?: number, padding?: number): number {
    if (!Number.isInteger(cols) || cols <= 0 || !isNum(sheetWidth)) { return 0; }
    const m = margins === undefined ? 2 * SHEET_MARGIN_PX : isNum(margins) ? margins : 0;
    const p = padding === undefined ? (cols - 1) * SHEET_PADDING_PX : isNum(padding) ? padding : 0;
    return Math.max(0, Math.floor((sheetWidth - m - p) / cols));
}

/**
 * Design §6.5: "the check step shows first when `frames ≥ 50 || (duration > 120 s && height > 1080)`
 * for sequences and sheets".
 */
export function needsBigJobCheck(frames: number, duration: number, height: number): boolean {
    return frames >= 50 || (duration > 120 && height > 1080);
}

/** "How long": `frames × 0.25 s` (design §6.2). The "a few seconds" floor is copy, applied by the caller. */
export function estimateSeconds(frames: number): number {
    return Math.max(0, isNum(frames) ? frames : 0) * SECONDS_PER_FRAME;
}

function isNum(v: unknown): v is number {
    return typeof v === 'number' && Number.isFinite(v);
}

/** Kotlin `Double.coerceIn(min, max)`. */
function coerceIn(v: number, min: number, max: number): number {
    return v < min ? min : v > max ? max : v;
}
